import { BestPracticeDataResponse } from '@deltek/specification-client/core/Models/bestPractice'
import { AllProjectResponse } from 'Models/project'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { shallow } from 'zustand/shallow'
import { ContentItemViewNodesType } from './types/contentItemViewNode.type'
import { HoveredItemStoreType } from './types/hoveredItemStore.type'

/**
 * Creates a zustand store for managing workspace hoveredItem state
 */
export const useHoveredItemStore = create<HoveredItemStoreType>()((set) => ({
    isMainMenuDisplayed: false,
    isBPMenuDisplayed: false,
    isManageOptionsDisplayed: false,
    isNotesMenuDisplayed: false,
    bestPracticeElementData: {} as BestPracticeDataResponse,
    isBestPracticeElementDataLoading: false,
    projectData: {} as Partial<AllProjectResponse>,
    isProjectDataLoading: false,
    hoveredNode: {} as Partial<ContentItemViewNodesType>,
    isProductTypeDeactivationDialogOpen: false,
    isMoveBlockMenuDisplayed: false,
    selectedMainMenuNode: {} as Partial<ContentItemViewNodesType>,
    dragAnchorElement: null,
    /** actions/setters */
    actions: {
        setIsMainMenuDisplayed: (displayed: boolean) => {
            set({ isMainMenuDisplayed: displayed })
        },
        setIsBPMenuDisplayed: (displayed: boolean) => {
            set({ isBPMenuDisplayed: displayed })
        },
        setIsManageOptionsDisplayed: (displayed: boolean) => {
            set({ isManageOptionsDisplayed: displayed })
        },
        setIsNotesMenuDisplayed: (displayed: boolean) => {
            set({ isNotesMenuDisplayed: displayed })
        },
        setBestPracticeElementData: (data: BestPracticeDataResponse) => {
            set({ bestPracticeElementData: data })
        },
        setHoveredNode: (node?: Partial<ContentItemViewNodesType>) => {
            set({ hoveredNode: node })
        },
        setProjectData: (data: AllProjectResponse) => {
            set({ projectData: data })
        },
        setIsProjectDataLoading: (loading: boolean) => {
            set({ isProjectDataLoading: loading })
        },
        setIsBestPracticeElementDataLoading: (loading: boolean) => {
            set({ isBestPracticeElementDataLoading: loading })
        },
        setIsProductTypeDeactivationDialogOpen: (value: boolean) => {
            set({ isProductTypeDeactivationDialogOpen: value })
        },
        setIsMoveBlockMenuDisplayed: (displayed) => {
            set({ isMoveBlockMenuDisplayed: displayed })
        },
        setSelectedMainMenuNode: (node) => {
            set({ selectedMainMenuNode: node })
        },
        setDragAnchorElement: (element) => {
            set({ dragAnchorElement: element })
        },
    },
}))
/** Zustand selector for getting the actions/setters. */
export const useHoveredItemActions = () =>
    useHoveredItemStore((state) => state.actions)
export const useIsMainMenuDisplayed = () =>
    useHoveredItemStore((state) => state.isMainMenuDisplayed)
export const useIsBPMenuDisplayed = () =>
    useHoveredItemStore((state) => state.isBPMenuDisplayed)
export const useIsManageOptionsDisplayed = () =>
    useHoveredItemStore((state) => state.isManageOptionsDisplayed)
export const useIsNotesMenuDisplayed = () =>
    useHoveredItemStore((state) => state.isNotesMenuDisplayed)

export const useBestPracticeElementData = () =>
    useHoveredItemStore((state) => state.bestPracticeElementData)
export const useIsBestPracticeElementDataLoading = () =>
    useHoveredItemStore((state) => state.isBestPracticeElementDataLoading)
export const useProjectData = () =>
    useHoveredItemStore((state) => state.projectData)
export const useIsProjectDataLoading = () =>
    useHoveredItemStore((state) => state.isProjectDataLoading)

export const useHoveredNode = () =>
    useHoveredItemStore((state) => state.hoveredNode)
export const useIsProductTypeDeactivationDialogOpen = () =>
    useHoveredItemStore((state) => state.isProductTypeDeactivationDialogOpen)
export const useIsMoveBlockMenuDisplayed = () =>
    useHoveredItemStore((state) => state.isMoveBlockMenuDisplayed)
export const useSelectedMainMenuNode = () =>
    useHoveredItemStore((state) => state.selectedMainMenuNode)
export const useDragAnchorElement = () =>
    useHoveredItemStore((state) => state.dragAnchorElement)

//  * @param selector - A function that takes the store state and returns the value to be selected.
//  * @returns The selected value from the store.

export const useShallowHoveredItems = <T>(
    selector: (state: HoveredItemStoreType) => T
): T => useHoveredItemStore(useShallow(selector))

/**
 * Optimized selector for TreeViewHoveredIcons component that uses shallow comparison
 * to prevent unnecessary re-renders when other parts of the state change.
 */
export const useHoverIconsState = () =>
    useHoveredItemStore(
        (state) => ({
            hoveredNode: state.hoveredNode,
            isMainMenuDisplayed: state.isMainMenuDisplayed,
            selectedMainMenuNode: state.selectedMainMenuNode,
            isManageOptionsDisplayed: state.isManageOptionsDisplayed,
            isBPMenuDisplayed: state.isBPMenuDisplayed,
        }),
        shallow
    )
/**
 * Optimized selector for TreeViewHoveredIcons component that uses shallow comparison
 * to prevent unnecessary re-renders when other parts of the state change.
 */
export const useHoverActionMenuState = () =>
    useHoveredItemStore(
        (state) => ({
            isNotesMenuDisplayed: state.isNotesMenuDisplayed,
            isMoveBlockMenuDisplayed: state.isMoveBlockMenuDisplayed,
        }),
        shallow
    )
